import React, { useState, useEffect } from "react";
import {
  RefreshCw,
  Play,
  Pause,
  Settings,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";
import n8nConfig from "../../config/n8n.config";

const ActiveWorkflows = () => {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        "http://localhost:3001/api/n8n/proxy/workflows",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            baseUrl: n8nConfig.baseUrl,
            apiKey: n8nConfig.apiKey,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      setWorkflows(data.data || []);
    } catch (error) {
      console.error("Error fetching workflows:", error);
      setError(error.message);
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkflow = async (workflowId, currentState) => {
    const action = currentState ? "deactivate" : "activate";

    try {
      console.log(`Attempting to ${action} workflow ${workflowId}`);

      const response = await fetch(
        `http://localhost:3001/api/n8n/proxy/workflow/${workflowId}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            baseUrl: n8nConfig.baseUrl,
            apiKey: n8nConfig.apiKey,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to ${action} workflow: ${response.status} - ${errorText}`,
        );
      }

      const data = await response.json();
      console.log(`Successfully ${action}d workflow:`, data);

      // Show success message briefly
      const originalError = error;
      setError(`✅ Workflow ${action}d successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setError(null);
      }, 3000);

      // Refresh workflow list to show updated status
      await fetchWorkflows();
    } catch (error) {
      console.error(`Error toggling workflow:`, error);
      setError(`Failed to ${action} workflow: ${error.message}`);
    }
  };

  const filteredWorkflows = workflows.filter(
    (workflow) =>
      workflow.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      workflow.id?.toString().includes(searchTerm),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Active Workflows
          </h1>
          <p className="text-slate-600">
            Monitor and manage your n8n workflows
          </p>
        </div>
        <PrimaryButton
          onClick={fetchWorkflows}
          icon={RefreshCw}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        onRefresh={fetchWorkflows}
        placeholder="Search workflows..."
      />

      {error && (
        <div
          className={`${error.startsWith("✅") ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"} border rounded-lg p-4 flex items-start gap-3`}
        >
          {error.startsWith("✅") ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h3
              className={`text-sm font-semibold ${error.startsWith("✅") ? "text-green-900" : "text-red-900"}`}
            >
              {error.startsWith("✅") ? "Success" : "Error Loading Workflows"}
            </h3>
            <p
              className={`text-sm ${error.startsWith("✅") ? "text-green-800" : "text-red-800"} mt-1`}
            >
              {error}
            </p>
            {!error.startsWith("✅") && (
              <p className="text-xs text-red-700 mt-2">
                Check that your n8n URL and API key are correct in{" "}
                <code className="bg-red-100 px-1 rounded">
                  src/config/n8n.config.js
                </code>
              </p>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Nodes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Last Run
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                </td>
              </tr>
            ) : filteredWorkflows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No workflows found. Connect to your n8n instance to see
                  workflows.
                </td>
              </tr>
            ) : (
              filteredWorkflows.map((workflow) => (
                <tr key={workflow.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {workflow.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                    {workflow.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        workflow.active
                          ? "bg-green-100 text-green-800"
                          : "bg-slate-100 text-slate-800"
                      }`}
                    >
                      {workflow.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {workflow.nodes?.length || 0} nodes
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                    {workflow.updatedAt
                      ? new Date(workflow.updatedAt).toLocaleString()
                      : "Never"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() =>
                          toggleWorkflow(workflow.id, workflow.active)
                        }
                        className={`p-2 rounded-lg transition-colors ${
                          workflow.active
                            ? "text-orange-600 hover:bg-orange-50"
                            : "text-green-600 hover:bg-green-50"
                        }`}
                        title={workflow.active ? "Deactivate" : "Activate"}
                      >
                        {workflow.active ? (
                          <Pause className="w-4 h-4" />
                        ) : (
                          <Play className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() =>
                          window.open(
                            `http://your-n8n-instance/workflow/${workflow.id}`,
                            "_blank",
                          )
                        }
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Open in n8n"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          📋 Configuration Required
        </h3>
        <p className="text-sm text-blue-800 mb-2">
          To connect to your n8n instance, update the API endpoint and
          authentication:
        </p>
        <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
          <li>Get your n8n API key from Settings → API</li>
          <li>Update the API base URL in this component</li>
          <li>Add your API key to the headers</li>
          <li>
            Uncomment the API calls in fetchWorkflows() and toggleWorkflow()
          </li>
        </ol>
      </div>
    </div>
  );
};

export default ActiveWorkflows;
